import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { ChevronUp, Heart, Minus, Plus, ShoppingBag } from "lucide-react";

import ProductSwatch from "../../components/swatches/ProductSwatch";
import { safeText } from "../../components/swatches/productSwatches";
import { formatProductMoney } from "../../utils/productPricing";

import "./PurchaseBox.css";

const CART_URL = "/cart";

const EMPTY_OBJECT = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]);

const OPTION_ACTION = Object.freeze({
  NONE: "none",
  ADD_TO_CART: "add_to_cart",
  VIEW_BASKET: "view_basket",
  ADJUST_QUANTITY: "adjust_quantity",
});

const SHOP_LABELS = Object.freeze({
  "ready-made": "Ready-Made",
  readyMade: "Ready-Made",
  standard: "Ready-Made",
  textile: "Textile",
  textiles: "Textile",
  haberdashery: "Haberdashery",
});

const PRODUCT_TYPE_LABELS = Object.freeze({
  standard: "Ready-Made",
  "ready-made": "Ready-Made",
  readyMade: "Ready-Made",
  textile: "Textile",
  textiles: "Textile",
  haberdashery: "Haberdashery",
});

const BLOCKING_PURCHASE_STATES = new Set([
  "SOLD_OUT",
  "RESERVED",
  "RESERVED_BY_ANOTHER_CUSTOMER",
  "INSUFFICIENT_STOCK",
  "COMING_SOON",
  "UNAVAILABLE",
]);

const BLOCKING_STOCK_STATUSES = new Set([
  "out_of_stock",
  "sold_out",
  "reserved",
  "requested_qty_exceeds_available",
  "insufficient_stock",
  "coming_soon",
  "unavailable",
]);

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : EMPTY_OBJECT;
}

function asArray(value) {
  return Array.isArray(value) ? value : EMPTY_ARRAY;
}

function formatMoney(value) {
  return formatProductMoney(value || 0);
}

function normalizeChoiceKey(value = "") {
  return safeText(value).toLowerCase().replace(/\s+/g, " ").trim();
}

function normalizeState(value = "") {
  return safeText(value).toUpperCase();
}

function normalizeStatus(value = "") {
  return safeText(value).toLowerCase();
}

function normalizeAction(value = "") {
  return normalizeChoiceKey(value).replace(/\s+/g, "_");
}

function formatInfoLabel(value = "") {
  return safeText(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatMappedValue(value) {
  const cleanValue = safeText(value);
  if (!cleanValue) return "";

  const directMatch =
    SHOP_LABELS[cleanValue] ||
    PRODUCT_TYPE_LABELS[cleanValue] ||
    SHOP_LABELS[normalizeChoiceKey(cleanValue)] ||
    PRODUCT_TYPE_LABELS[normalizeChoiceKey(cleanValue)];

  if (directMatch) return directMatch;

  return formatInfoLabel(cleanValue);
}

function formatInfoValue(value, label = "") {
  const cleanLabel = normalizeChoiceKey(label);

  if (Array.isArray(value)) {
    return value
      .map((item) => formatMappedValue(item))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "";
  }

  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value) || value <= 0) return "";
    return String(value);
  }

  if (value && typeof value === "object") {
    return "";
  }

  if (
    cleanLabel.includes("product type") ||
    cleanLabel === "type" ||
    cleanLabel.includes("shop")
  ) {
    return formatMappedValue(value);
  }

  return safeText(value);
}

function hasSimilarInfoLabel(rows, labels = []) {
  return rows.some(([existingLabel]) => {
    const cleanExistingLabel = normalizeChoiceKey(existingLabel);

    return labels.some((label) =>
      cleanExistingLabel.includes(normalizeChoiceKey(label)),
    );
  });
}

function addInfoRow(rows, label, value) {
  const cleanLabel = formatInfoLabel(label);
  const cleanValue = formatInfoValue(value, label);

  if (!cleanLabel || !cleanValue) return;

  const alreadyExists = rows.some(
    ([existingLabel, existingValue]) =>
      normalizeChoiceKey(existingLabel) === normalizeChoiceKey(cleanLabel) ||
      normalizeChoiceKey(existingValue) === normalizeChoiceKey(cleanValue),
  );

  if (!alreadyExists) {
    rows.push([cleanLabel, cleanValue]);
  }
}

function addMeasuredInfoRow(rows, label, value, unit) {
  const number = Number(value);

  if (!Number.isFinite(number) || number <= 0) return;

  addInfoRow(rows, label, `${number} ${unit}`);
}

function getProductTypeLabel(product = {}) {
  const safeProduct = asObject(product);

  return (
    PRODUCT_TYPE_LABELS[safeProduct.productType] ||
    SHOP_LABELS[safeProduct.shopKey] ||
    formatMappedValue(safeProduct.productType || safeProduct.shopKey)
  );
}

function getOptionParams(option) {
  const safeOption = asObject(option);
  const value = asObject(safeOption.value);

  return {
    variantSku: safeText(
      safeOption.variantSku || value.variantSku,
    ).toUpperCase(),
    variantType: safeText(
      safeOption.variantType || value.variantType,
    ).toLowerCase(),
  };
}

function getOptionKey(option) {
  const safeOption = asObject(option);
  const params = getOptionParams(safeOption);

  return [
    params.variantSku || "no-sku",
    params.variantType || "standard",
    safeText(safeOption.label) || "option",
  ].join("::");
}

function getOptionColourLabel(option) {
  const safeOption = asObject(option);

  return safeText(
    safeOption.color ||
      safeOption.colour ||
      safeOption.colorName ||
      safeOption.colourName ||
      safeOption.colorwayLabel ||
      safeOption.colourwayLabel,
  );
}

function getOptionSizeLabel(option) {
  const safeOption = asObject(option);
  return safeText(safeOption.size);
}

function getOptionColourKey(option) {
  return normalizeChoiceKey(getOptionColourLabel(option));
}

function getOptionSizeKey(option) {
  return normalizeChoiceKey(getOptionSizeLabel(option));
}

function getOptionDisplayLabel(option = {}) {
  const safeOption = asObject(option);

  return safeText(
    safeOption.size ||
      safeOption.label ||
      safeOption.name ||
      safeOption.variantName ||
      safeOption.unitLabel ||
      safeOption.variantType ||
      "Option",
  );
}

function cleanBackendStockMessage(message = "") {
  const text = safeText(message);
  const normalized = text.toLowerCase();

  if (!text) return "";

  if (
    normalized.includes("already have all available stock") ||
    normalized.includes("maximum quantity already") ||
    normalized.includes("max in basket") ||
    normalized.includes("max in cart")
  ) {
    return "This item is already in your bag.";
  }

  if (
    normalized.includes("temporarily reserved") ||
    normalized.includes("reserved by another customer") ||
    normalized === "reserved"
  ) {
    return "Currently unavailable.";
  }

  if (
    normalized.includes("shared roll stock is already allocated") ||
    normalized.includes("blocked by another cut")
  ) {
    return "Choose another cut or view your basket.";
  }

  if (normalized.includes("can be held")) {
    return text.replace(/can be held/gi, "is available");
  }

  if (normalized === "out of stock") return "Sold out";

  return text;
}

function getBackendMessage(option = {}) {
  const safeOption = asObject(option);

  return cleanBackendStockMessage(
    safeOption.customerMessage ||
      safeOption.purchaseMessage ||
      safeOption.availableLabel ||
      safeOption.statusReason ||
      safeOption.stockStatusReason ||
      safeOption.disabledReason ||
      safeOption.unavailableReason ||
      safeOption.stockStatusMessage ||
      safeOption.message,
  );
}

function getRemainingAddableQty(option = {}) {
  const safeOption = asObject(option);
  const value = Number(
    safeOption.remainingAddableQty ??
      safeOption.maxAddableQty ??
      safeOption.available ??
      0,
  );

  if (!Number.isFinite(value)) return 0;

  return Math.max(0, Math.floor(value));
}

function getAlreadyInCartQty(option = {}) {
  const safeOption = asObject(option);
  const value = Number(safeOption.alreadyInCartQty ?? 0);

  if (!Number.isFinite(value)) return 0;

  return Math.max(0, Math.floor(value));
}

function getPrimaryAction(option = {}, fallback = OPTION_ACTION.NONE) {
  return normalizeAction(option.primaryAction) || fallback;
}

function optionUsesSharedStock(option = {}) {
  const safeOption = asObject(option);
  const status = normalizeStatus(safeOption.status || safeOption.stockStatus);
  const state = normalizeState(
    safeOption.purchaseState || safeOption.unavailableState,
  );

  return Boolean(
    safeOption.sharedStock?.isShared ||
    safeOption.sharedPoolReducedByCart ||
    safeOption.sharedPoolBlockedByCart ||
    state === "SHARED_STOCK_BLOCKED_BY_CART" ||
    status === "shared_stock_blocked_by_cart" ||
    status === "shared_stock_reduced_by_cart" ||
    status === "shared_stock_exhausted_in_cart",
  );
}

function isBasketActionState(purchaseState = {}) {
  const safePurchaseState = asObject(purchaseState);

  return (
    safePurchaseState.primaryAction === OPTION_ACTION.VIEW_BASKET ||
    safePurchaseState.state === "MAX_IN_CART" ||
    safePurchaseState.state === "SHARED_STOCK_EXHAUSTED_IN_CART" ||
    safePurchaseState.status === "max_in_cart" ||
    safePurchaseState.status === "shared_stock_exhausted_in_cart"
  );
}

function getSharedStockMessage(option = {}) {
  const safeOption = asObject(option);
  const backendMessage = getBackendMessage(safeOption);

  if (backendMessage) return backendMessage;

  if (safeOption.sharedPoolBlockedByCart) {
    return "Choose another cut or view your basket.";
  }

  const maxAddableQty = getRemainingAddableQty(safeOption);

  if (maxAddableQty === 1) {
    return "Only 1 more can be added from this roll.";
  }

  if (maxAddableQty > 1 && maxAddableQty <= 3) {
    return `Only ${maxAddableQty} more can be added from this roll.`;
  }

  return "These options use the same roll stock.";
}

function getOptionUnavailableLabel(option = {}) {
  return getBackendMessage(option) || "Unavailable";
}

function getCustomerAvailabilityLabel(option = {}) {
  const safeOption = asObject(option);
  const state = normalizeState(
    safeOption.purchaseState || safeOption.unavailableState,
  );
  const status = normalizeStatus(safeOption.status || safeOption.stockStatus);

  const backendMessage = getBackendMessage(safeOption);
  if (backendMessage) return backendMessage;

  if (
    safeOption.isInBasket ||
    safeOption.isMaxedInCart ||
    getAlreadyInCartQty(safeOption) > 0 ||
    state === "MAX_IN_CART" ||
    status === "max_in_cart"
  ) {
    return "In your bag";
  }

  if (status === "shared_stock_exhausted_in_cart") return "In your bag";

  if (
    state === "SHARED_STOCK_BLOCKED_BY_CART" ||
    status === "shared_stock_blocked_by_cart"
  ) {
    return "Choose another cut";
  }

  if (optionUsesSharedStock(safeOption)) {
    return getSharedStockMessage(safeOption);
  }

  if (state === "SOLD_OUT" || status === "out_of_stock") return "Sold out";

  if (state === "RESERVED" || state === "RESERVED_BY_ANOTHER_CUSTOMER") {
    return "Currently unavailable";
  }

  if (
    state === "INSUFFICIENT_STOCK" ||
    status === "requested_qty_exceeds_available" ||
    status === "insufficient_stock"
  ) {
    const maxAddableQty = getRemainingAddableQty(safeOption);

    if (maxAddableQty === 1) return "Only 1 available";

    if (maxAddableQty > 1) {
      return `Only ${maxAddableQty} available`;
    }

    return "Limited stock";
  }

  if (state === "COMING_SOON" || status === "coming_soon") return "Coming soon";

  if (state === "UNAVAILABLE" || status === "unavailable") {
    return getOptionUnavailableLabel(safeOption);
  }

  const available = Math.floor(Number(safeOption.available || 0));
  const maxAddableQty = getRemainingAddableQty(safeOption);

  const customerQty =
    Number.isFinite(maxAddableQty) && maxAddableQty > 0
      ? maxAddableQty
      : available;

  if (customerQty <= 0) return "Unavailable";
  if (customerQty === 1) return "Only 1 available";
  if (customerQty <= 3) return `Only ${customerQty} available`;

  return "In stock";
}

function getOptionPurchaseState(option = {}) {
  const safeOption = asObject(option);

  if (
    safeOption.isDisplayable === false ||
    safeOption.displayInOptions === false
  ) {
    return {
      state: "HIDDEN",
      status: "",
      isDisplayable: false,
      isSelectable: false,
      isAddable: false,
      label: "Hidden",
      message: "",
      primaryAction: OPTION_ACTION.NONE,
    };
  }

  const state = normalizeState(
    safeOption.purchaseState ||
      safeOption.unavailableState ||
      safeOption.reason,
  );

  const status = normalizeStatus(safeOption.status || safeOption.stockStatus);
  const backendMessage = getBackendMessage(safeOption);
  const primaryAction = getPrimaryAction(safeOption);

  const hasExplicitSelectFalse =
    safeOption.canSelect === false || safeOption.isSelectable === false;

  const isInBasket =
    safeOption.isInBasket ||
    safeOption.isMaxedInCart ||
    getAlreadyInCartQty(safeOption) > 0 ||
    state === "MAX_IN_CART" ||
    status === "max_in_cart" ||
    primaryAction === OPTION_ACTION.VIEW_BASKET;

  if (isInBasket) {
    return {
      state: "MAX_IN_CART",
      status: "max_in_cart",
      isDisplayable: true,
      isSelectable: true,
      isAddable: false,
      label: "In your bag",
      message: backendMessage || "This item is already in your bag.",
      primaryAction: OPTION_ACTION.VIEW_BASKET,
    };
  }

  if (
    status === "shared_stock_exhausted_in_cart" ||
    state === "SHARED_STOCK_EXHAUSTED_IN_CART"
  ) {
    return {
      state: "SHARED_STOCK_EXHAUSTED_IN_CART",
      status: "shared_stock_exhausted_in_cart",
      isDisplayable: true,
      isSelectable: true,
      isAddable: false,
      label: "In your bag",
      message: backendMessage || "This item is already in your bag.",
      primaryAction: OPTION_ACTION.VIEW_BASKET,
    };
  }

  if (
    state === "SHARED_STOCK_BLOCKED_BY_CART" ||
    status === "shared_stock_blocked_by_cart" ||
    safeOption.sharedPoolBlockedByCart === true
  ) {
    return {
      state: "SHARED_STOCK_BLOCKED_BY_CART",
      status: "shared_stock_blocked_by_cart",
      isDisplayable: true,
      isSelectable: !hasExplicitSelectFalse,
      isAddable: false,
      label: "Choose another cut",
      message: backendMessage || "Choose another cut or view your basket.",
      primaryAction:
        primaryAction === OPTION_ACTION.VIEW_BASKET
          ? OPTION_ACTION.VIEW_BASKET
          : OPTION_ACTION.NONE,
    };
  }

  if (
    state === "INSUFFICIENT_STOCK" ||
    status === "requested_qty_exceeds_available" ||
    status === "insufficient_stock"
  ) {
    const message =
      backendMessage || "The requested quantity is not available.";

    return {
      state: "INSUFFICIENT_STOCK",
      status: status || "requested_qty_exceeds_available",
      isDisplayable: true,
      isSelectable: !hasExplicitSelectFalse,
      isAddable: false,
      label: "Limited stock",
      message,
      primaryAction: OPTION_ACTION.ADJUST_QUANTITY,
    };
  }

  if (
    state === "RESERVED" ||
    state === "RESERVED_BY_ANOTHER_CUSTOMER" ||
    status === "reserved" ||
    safeOption.reserved === true
  ) {
    const message = backendMessage || "Currently unavailable.";

    return {
      state: "RESERVED",
      status: "reserved",
      isDisplayable: true,
      isSelectable: false,
      isAddable: false,
      label: "Currently unavailable",
      message,
      primaryAction: OPTION_ACTION.NONE,
    };
  }

  if (
    state === "COMING_SOON" ||
    status === "coming_soon" ||
    safeOption.comingSoon
  ) {
    const message = backendMessage || "This selection is coming soon.";

    return {
      state: "COMING_SOON",
      status: "coming_soon",
      isDisplayable: true,
      isSelectable: false,
      isAddable: false,
      label: "Coming soon",
      message,
      primaryAction: OPTION_ACTION.NONE,
    };
  }

  if (
    state === "SOLD_OUT" ||
    status === "out_of_stock" ||
    status === "sold_out" ||
    safeOption.soldOut === true
  ) {
    const message = backendMessage || "This selection is sold out.";

    return {
      state: "SOLD_OUT",
      status: "out_of_stock",
      isDisplayable: true,
      isSelectable: false,
      isAddable: false,
      label: "Sold out",
      message,
      primaryAction: OPTION_ACTION.NONE,
    };
  }

  if (
    state === "UNAVAILABLE" ||
    status === "unavailable" ||
    safeOption.disabled === true ||
    safeOption.isAvailable === false
  ) {
    const message = backendMessage || getOptionUnavailableLabel(safeOption);

    return {
      state: "UNAVAILABLE",
      status: status || "unavailable",
      isDisplayable: true,
      isSelectable: false,
      isAddable: false,
      label: message || "Unavailable",
      message,
      primaryAction: OPTION_ACTION.NONE,
    };
  }

  if (
    BLOCKING_PURCHASE_STATES.has(state) ||
    BLOCKING_STOCK_STATUSES.has(status)
  ) {
    const message = backendMessage || getCustomerAvailabilityLabel(safeOption);

    return {
      state: state || "UNAVAILABLE",
      status: status || "unavailable",
      isDisplayable: true,
      isSelectable: false,
      isAddable: false,
      label: message,
      message,
      primaryAction: OPTION_ACTION.NONE,
    };
  }

  const maxAddableQty = getRemainingAddableQty(safeOption);
  const available = Number(safeOption.available);

  if (safeOption.canAddToCart === false || safeOption.isAddable === false) {
    const message = backendMessage || getCustomerAvailabilityLabel(safeOption);

    return {
      state: "UNAVAILABLE",
      status: status || "unavailable",
      isDisplayable: true,
      isSelectable: !hasExplicitSelectFalse,
      isAddable: false,
      label: message || "Unavailable",
      message,
      primaryAction: primaryAction || OPTION_ACTION.NONE,
    };
  }

  if (Number.isFinite(available) && available <= 0 && maxAddableQty <= 0) {
    const message = backendMessage || "This selection is sold out.";

    return {
      state: "SOLD_OUT",
      status: "out_of_stock",
      isDisplayable: true,
      isSelectable: false,
      isAddable: false,
      label: "Sold out",
      message,
      primaryAction: OPTION_ACTION.NONE,
    };
  }

  const isAddable =
    maxAddableQty > 0 &&
    safeOption.disabled !== true &&
    safeOption.canAddToCart !== false &&
    safeOption.isAddable !== false &&
    safeOption.isAvailable !== false;

  return {
    state: isAddable ? "ADDABLE" : "UNAVAILABLE",
    status:
      status === "shared_stock_reduced_by_cart"
        ? "shared_stock_reduced_by_cart"
        : isAddable
          ? "available"
          : status || "unavailable",
    isDisplayable: true,
    isSelectable: isAddable || !hasExplicitSelectFalse,
    isAddable,
    label: getCustomerAvailabilityLabel(safeOption),
    message: "",
    primaryAction: isAddable ? OPTION_ACTION.ADD_TO_CART : OPTION_ACTION.NONE,
  };
}

function getSharedStockNote(selectedOption) {
  const safeSelectedOption = asObject(selectedOption);
  const purchaseState = getOptionPurchaseState(safeSelectedOption);

  if (!optionUsesSharedStock(safeSelectedOption)) return "";

  if (purchaseState.state === "SHARED_STOCK_BLOCKED_BY_CART") {
    return "Per metre, half metre and related cuts use the same roll. Choose another cut or review your basket.";
  }

  if (purchaseState.state === "MAX_IN_CART") {
    return "";
  }

  return "Per metre, half metre and related cuts use the same roll stock.";
}

function getOptionStateClass(purchaseState = {}) {
  return `is-${safeText(purchaseState.state).toLowerCase().replace(/_/g, "-")}`;
}

function firstNonEmptyParagraph(paragraphs = []) {
  return asArray(paragraphs).find((paragraph) => safeText(paragraph)) || "";
}

function getProductCode(product = {}, selectedOption = null) {
  const safeProduct = asObject(product);
  const safeSelectedOption = asObject(selectedOption);

  return safeText(
    safeSelectedOption.sku ||
      safeSelectedOption.variantSku ||
      safeProduct.sku ||
      safeProduct.productCode ||
      safeProduct.code ||
      safeProduct._id ||
      safeProduct.id,
  );
}

function getAddButtonLabel({
  addingToCart,
  resolveLoading,
  canPurchase,
  selectedOption,
  availabilityText,
}) {
  if (addingToCart) return "Adding...";
  if (resolveLoading) return "Checking...";
  if (canPurchase) return "Add to Basket";

  const purchaseState = getOptionPurchaseState(selectedOption);

  if (isBasketActionState(purchaseState)) return "View Basket";
  if (purchaseState.primaryAction === OPTION_ACTION.VIEW_BASKET)
    return "View Basket";
  if (purchaseState.state === "INSUFFICIENT_STOCK") return "Limited stock";
  if (purchaseState.state === "SOLD_OUT") return "Sold out";
  if (purchaseState.state === "RESERVED") return "Currently unavailable";
  if (purchaseState.state === "COMING_SOON") return "Coming soon";

  return availabilityText || purchaseState.label || "Unavailable";
}

function getStatusBadgeMeta(selectedOption) {
  const safeSelectedOption = asObject(selectedOption);

  if (safeSelectedOption === EMPTY_OBJECT) {
    return { label: "Select option", tone: "neutral" };
  }

  const purchaseState = getOptionPurchaseState(safeSelectedOption);
  const status = normalizeStatus(
    safeSelectedOption.status || safeSelectedOption.stockStatus,
  );
  const maxAddableQty = getRemainingAddableQty(safeSelectedOption);

  if (purchaseState.state === "ADDABLE") {
    if (status === "shared_stock_reduced_by_cart") {
      return { label: "Shared stock", tone: "low" };
    }

    if (maxAddableQty > 0 && maxAddableQty <= 3) {
      return {
        label:
          maxAddableQty === 1 ? "Only 1 left" : `Only ${maxAddableQty} left`,
        tone: "low",
      };
    }

    return { label: "In stock", tone: "positive" };
  }

  if (purchaseState.state === "MAX_IN_CART") {
    return { label: "In your bag", tone: "positive" };
  }

  if (purchaseState.state === "SHARED_STOCK_EXHAUSTED_IN_CART") {
    return { label: "In your bag", tone: "positive" };
  }

  if (purchaseState.state === "SOLD_OUT")
    return { label: "Sold out", tone: "warning" };
  if (purchaseState.state === "RESERVED") {
    return { label: "Currently unavailable", tone: "warning" };
  }

  if (purchaseState.state === "INSUFFICIENT_STOCK") {
    return { label: "Limited stock", tone: "warning" };
  }

  if (purchaseState.state === "SHARED_STOCK_BLOCKED_BY_CART") {
    return { label: "Choose another cut", tone: "warning" };
  }

  if (purchaseState.state === "COMING_SOON") {
    return { label: "Coming soon", tone: "neutral" };
  }

  return { label: "Unavailable", tone: "neutral" };
}

function buildSelectedSummary({
  selectedColourLabel,
  selectedSizeLabel,
  selectedOption,
}) {
  const safeSelectedOption = asObject(selectedOption);
  const parts = [];

  if (selectedColourLabel) parts.push(safeText(selectedColourLabel));

  const optionLabel =
    selectedSizeLabel ||
    getOptionSizeLabel(safeSelectedOption) ||
    getOptionDisplayLabel(safeSelectedOption);

  if (optionLabel && !parts.includes(optionLabel)) {
    parts.push(optionLabel);
  }

  return parts.filter(Boolean).join(" / ");
}

function buildProductInformationRows({
  product = {},
  selectedOption = {},
  specificationRows = [],
  descriptionParagraphs = [],
}) {
  const safeProduct = asObject(product);
  const safeSelectedOption = asObject(selectedOption);
  const readyMade = asObject(safeProduct.readyMade);
  const textile = asObject(safeProduct.textile);
  const haberdashery = asObject(safeProduct.haberdashery);

  const rows = [];

  asArray(specificationRows).forEach((row) => {
    if (!Array.isArray(row)) return;

    const [label, value] = row;
    addInfoRow(rows, label, value);
  });

  if (!hasSimilarInfoLabel(rows, ["product type", "type"])) {
    addInfoRow(rows, "Product Type", getProductTypeLabel(safeProduct));
  }

  const careFabricText = asArray(descriptionParagraphs)
    .slice(1)
    .map((paragraph) => safeText(paragraph))
    .filter(Boolean)
    .join(" ");

  if (!hasSimilarInfoLabel(rows, ["care", "fabric"])) {
    addInfoRow(rows, "Care & Fabric", careFabricText);
  }

  addInfoRow(rows, "Colour", safeSelectedOption.color || safeProduct.color);
  addInfoRow(rows, "Colours", safeSelectedOption.colors || safeProduct.colors);
  addInfoRow(rows, "Size", safeSelectedOption.size || safeProduct.size);
  addInfoRow(rows, "Sizes", safeSelectedOption.sizes || safeProduct.sizes);
  addInfoRow(
    rows,
    "Material",
    safeSelectedOption.material || safeProduct.material,
  );
  addInfoRow(
    rows,
    "Materials",
    safeSelectedOption.materials || safeProduct.materials,
  );
  addInfoRow(
    rows,
    "Patterns",
    safeSelectedOption.patterns || safeProduct.patterns,
  );
  addInfoRow(rows, "Length", safeSelectedOption.length);
  addInfoRow(rows, "Origin", safeProduct.origin);
  addInfoRow(rows, "Best For", safeProduct.usage);

  if (!hasSimilarInfoLabel(rows, ["care instructions", "care", "fabric"])) {
    addInfoRow(rows, "Care", safeProduct.careInstructions);
  }

  addInfoRow(
    rows,
    "Unit",
    safeSelectedOption.unitLabel || safeProduct.unitLabel,
  );

  const itemsPerPack = Number(
    safeSelectedOption.itemsPerPack ?? safeProduct.itemsPerPack,
  );

  if (Number.isFinite(itemsPerPack) && itemsPerPack > 1) {
    addInfoRow(rows, "Items Per Pack", itemsPerPack);
  }

  addInfoRow(rows, "Gender", readyMade.gender);
  addInfoRow(rows, "Era", readyMade.era);
  addInfoRow(rows, "Fit", readyMade.fit);
  addInfoRow(rows, "Silhouette", readyMade.silhouette);
  addInfoRow(rows, "Closure", readyMade.closure);

  addInfoRow(rows, "Textile Sell Mode", textile.sellMode);
  addInfoRow(rows, "Textile Unit Type", textile.unitType);
  addInfoRow(rows, "Textile Unit Value", textile.unitValue);
  addInfoRow(rows, "Sample Limit", textile.sampleLimit);

  addInfoRow(rows, "Haberdashery Sell Mode", haberdashery.sellMode);
  addInfoRow(rows, "Haberdashery Unit Type", haberdashery.unitType);

  const haberdasheryUnitValue = Number(haberdashery.unitValue);
  if (Number.isFinite(haberdasheryUnitValue) && haberdasheryUnitValue !== 1) {
    addInfoRow(rows, "Haberdashery Unit Value", haberdasheryUnitValue);
  }

  addInfoRow(rows, "Finish", haberdashery.finish);
  addMeasuredInfoRow(rows, "Width", haberdashery.widthMm, "mm");
  addMeasuredInfoRow(rows, "Diameter", haberdashery.diameterMm, "mm");
  addInfoRow(rows, "Compatibility", haberdashery.compatibility);
  addInfoRow(rows, "Compatibilities", haberdashery.compatibilities);

  if (safeProduct.exclusive) addInfoRow(rows, "Exclusive", true);
  if (safeProduct.limitedEdition) addInfoRow(rows, "Limited Edition", true);
  if (safeProduct.isVintage) addInfoRow(rows, "Vintage", true);
  if (safeProduct.isCustomMade) addInfoRow(rows, "Custom Made", true);

  return rows;
}

function AccordionRow({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className={clsx("purchase-box__accordion", open && "is-open")}>
      <button
        type="button"
        className="purchase-box__accordion-button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span>{title}</span>
        <ChevronUp
          size={18}
          className={clsx("purchase-box__accordion-icon", !open && "is-closed")}
        />
      </button>

      {open ? (
        <div className="purchase-box__accordion-body">{children}</div>
      ) : null}
    </section>
  );
}

export default function PurchaseBox({
  product = {},
  brand,
  selectedPrice,
  selectedOption,
  selectedOptionKey,
  optionLabel = "Size",
  colourItems = [],
  sizeItems = [],
  options = [],
  selectedColourLabel,
  selectedSizeLabel,
  hasStock,
  availabilityText,
  qty = 1,
  maxQty = 1,
  resolveLoading,
  resolveError,
  addingToCart,
  basketFeedback = {},
  madeToMeasureMode = false,
  madeToMeasureStepLabel = "",
  madeToMeasureCanSelect = false,
  madeToMeasureLoading = false,
  madeToMeasureFeedback = "",
  canPurchase,
  descriptionParagraphs = [],
  specificationRows = [],
  isSaved = false,
  savingProduct = false,
  sizeGuideUrl = "/size-guide",
  onChoiceSelect,
  onOptionSelect,
  onQtyChange,
  onAddToBasket,
  onSelectForMadeToMeasure,
  onSaveProduct,
}) {
  const safeProduct = asObject(product);
  const safeOptions = asArray(options);
  const safeColourItems = asArray(colourItems);
  const safeSizeItems = asArray(sizeItems);
  const safeDescriptionParagraphs = asArray(descriptionParagraphs);
  const safeSpecificationRows = asArray(specificationRows);

  const displayableOptions = useMemo(
    () =>
      safeOptions.filter((option) => {
        const purchaseState = getOptionPurchaseState(option);
        return purchaseState.isDisplayable;
      }),
    [safeOptions],
  );

  const visibleSizeItems = useMemo(() => {
    if (safeSizeItems.length) return safeSizeItems;

    return displayableOptions.map((option) => {
      const key = getOptionKey(option);
      const purchaseState = getOptionPurchaseState(option);

      return {
        key,
        label: getOptionDisplayLabel(option),
        optionKeys: [key],
        isAddable: purchaseState.isAddable,
        isSelectable: purchaseState.isSelectable,
        purchaseState,
        option,
      };
    });
  }, [displayableOptions, safeSizeItems]);

  const selectedPurchaseState = getOptionPurchaseState(selectedOption);
  const sharedStockNote = getSharedStockNote(selectedOption);
  const statusMeta = getStatusBadgeMeta(selectedOption);
  const variantShortDescription = safeText(
    selectedOption?.shortDescription,
  );

  const introDescription = firstNonEmptyParagraph(safeDescriptionParagraphs);
  const productCode = getProductCode(safeProduct, selectedOption);

  const productInformationRows = useMemo(
    () =>
      buildProductInformationRows({
        product: safeProduct,
        selectedOption,
        specificationRows: safeSpecificationRows,
        descriptionParagraphs: safeDescriptionParagraphs,
      }),
    [
      safeProduct,
      selectedOption,
      safeSpecificationRows,
      safeDescriptionParagraphs,
    ],
  );

  const safeQty = Math.max(1, Number(qty || 1));
  const safeMaxQty = Math.max(0, Number(maxQty || 0));
  const showQty = Boolean(
    safeMaxQty > 1 &&
      (canPurchase || selectedPurchaseState.isAddable || hasStock),
  );
  const canDecreaseQty = Boolean(showQty && hasStock) && safeQty > 1;
  const canIncreaseQty =
    Boolean(showQty && hasStock) && safeMaxQty > 0 && safeQty < safeMaxQty;

  const addButtonLabel = getAddButtonLabel({
    addingToCart,
    resolveLoading,
    canPurchase,
    selectedOption,
    availabilityText,
  });

  const shouldShowBasketAction =
    !canPurchase &&
    (isBasketActionState(selectedPurchaseState) ||
      selectedPurchaseState.primaryAction === OPTION_ACTION.VIEW_BASKET);

  const selectedSummary = buildSelectedSummary({
    selectedColourLabel,
    selectedSizeLabel,
    selectedOption,
  });

  const showSaveButton = typeof onSaveProduct === "function";
  const saveDisabled = Boolean(savingProduct);
  const showMadeToMeasureAction = Boolean(
    madeToMeasureMode && typeof onSelectForMadeToMeasure === "function",
  );

  const handleChoiceSelect = (item) => {
    if (typeof onChoiceSelect === "function") {
      onChoiceSelect(item);
    }
  };

  const handleOptionSelect = (option) => {
    if (typeof onOptionSelect === "function") {
      onOptionSelect(option);
    }
  };

  const handleQtyChange = (nextQty) => {
    if (typeof onQtyChange === "function") {
      onQtyChange(nextQty);
    }
  };

  const handleAddToBasket = () => {
    if (typeof onAddToBasket === "function") {
      onAddToBasket();
    }
  };

  const handleSelectForMadeToMeasure = () => {
    if (typeof onSelectForMadeToMeasure === "function") {
      onSelectForMadeToMeasure();
    }
  };

  return (
    <>
      <aside className="purchase-box" aria-label="Product purchase panel">
        <header className="purchase-box__header">
          <p className="purchase-box__brand">{safeProduct.brand || brand}</p>
          <h1 className="purchase-box__title">{safeProduct.name}</h1>

          <div className="purchase-box__price-row">
            <p className="purchase-box__price">{formatMoney(selectedPrice)}</p>
          </div>

          {selectedSummary ? (
            <p className="purchase-box__selection-summary" aria-live="polite">
              Selected: <strong>{selectedSummary}</strong>
            </p>
          ) : null}

          {variantShortDescription ? (
            <p className="purchase-box__variant-description">
              {variantShortDescription}
            </p>
          ) : null}

          {availabilityText ? (
            <p
              className={clsx("purchase-box__availability", {
                "is-available": hasStock,
                "is-unavailable": !hasStock,
              })}
              aria-live="polite"
            >
              <span
                className="purchase-box__availability-dot"
                aria-hidden="true"
              />
              <span>{availabilityText}</span>
            </p>
          ) : null}
        </header>

        {safeColourItems.length ? (
          <section className="purchase-box__section">
            <div className="purchase-box__section-head">
              <h2>Fabric / Colour</h2>
              {selectedColourLabel ? (
                <strong>{selectedColourLabel}</strong>
              ) : null}
            </div>

            <div className="purchase-box__swatches">
              {safeColourItems.map((item) => {
                const safeItem = asObject(item);
                const active =
                  asArray(safeItem.optionKeys).includes(selectedOptionKey) ||
                  safeItem.key === getOptionColourKey(selectedOption);

                const isSelectable = safeItem.isSelectable !== false;

                return (
                  <button
                    key={safeItem.key || safeItem.ariaLabel}
                    type="button"
                    className={clsx("purchase-box__swatch-button", {
                      "is-active": active,
                      "is-disabled": !isSelectable,
                      "is-not-addable": safeItem.isAddable === false,
                    })}
                    disabled={!isSelectable}
                    onClick={() => {
                      if (!isSelectable) return;
                      handleChoiceSelect(safeItem);
                    }}
                    aria-label={safeItem.ariaLabel}
                    aria-pressed={active}
                    aria-disabled={!isSelectable}
                    title={
                      isSelectable
                        ? safeItem.ariaLabel
                        : `${safeItem.ariaLabel}. Unavailable.`
                    }
                  >
                    <ProductSwatch
                      swatch={safeItem.swatch}
                      className="pdp-option__swatch"
                      label={safeItem.ariaLabel}
                      role="img"
                    />
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {visibleSizeItems.length > 1 ? (
          <section className="purchase-box__section">
            <div className="purchase-box__section-head purchase-box__section-head--between">
              <div className="purchase-box__section-head-copy">
                <h2>{optionLabel}</h2>
                {selectedSizeLabel ? (
                  <strong>{selectedSizeLabel}</strong>
                ) : null}
              </div>

              {/* <Link to={sizeGuideUrl} className="purchase-box__inline-link">
                Size Guide
              </Link> */}
            </div>

            <div
              className="purchase-box__sizes"
              aria-label={`${optionLabel} options`}
            >
              {visibleSizeItems.map((item) => {
                const safeItem = asObject(item);
                const itemOptionKeys = asArray(safeItem.optionKeys);

                const option =
                  safeItem.option ||
                  displayableOptions.find((candidate) =>
                    itemOptionKeys.includes(getOptionKey(candidate)),
                  );

                const purchaseState =
                  safeItem.purchaseState || getOptionPurchaseState(option);

                const disabled =
                  purchaseState.isSelectable === false ||
                  safeItem.isSelectable === false;

                const active =
                  itemOptionKeys.includes(selectedOptionKey) ||
                  safeItem.key === getOptionSizeKey(selectedOption);

                return (
                  <button
                    key={safeItem.key || safeItem.label}
                    type="button"
                    className={clsx(
                      "purchase-box__size-button",
                      getOptionStateClass(purchaseState),
                      {
                        "is-active": active,
                        "is-disabled": disabled,
                        "is-not-addable": !purchaseState.isAddable,
                      },
                    )}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;

                      if (option) {
                        handleOptionSelect(option);
                      } else {
                        handleChoiceSelect(safeItem);
                      }
                    }}
                    aria-pressed={active}
                    aria-disabled={disabled}
                    title={
                      !disabled
                        ? `Select ${optionLabel.toLowerCase()} ${safeItem.label}`
                        : purchaseState.message || purchaseState.label
                    }
                  >
                    <span>{safeItem.label}</span>
                    {!purchaseState.isAddable ? (
                      <small>{purchaseState.label}</small>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {sharedStockNote ? (
              <p className="purchase-box__micro-copy">{sharedStockNote}</p>
            ) : null}
          </section>
        ) : null}

        {!visibleSizeItems.length && displayableOptions.length ? (
          <section className="purchase-box__section">
            <div className="purchase-box__section-head">
              <h2>{optionLabel}</h2>
            </div>

            <div className="purchase-box__options">
              {displayableOptions.map((option) => {
                const key = getOptionKey(option);
                const purchaseState = getOptionPurchaseState(option);
                const active = key === selectedOptionKey;
                const disabled = purchaseState.isSelectable === false;

                return (
                  <button
                    key={key}
                    type="button"
                    className={clsx(
                      "purchase-box__option",
                      getOptionStateClass(purchaseState),
                      {
                        "is-active": active,
                        "is-disabled": disabled,
                        "is-not-addable": !purchaseState.isAddable,
                      },
                    )}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      handleOptionSelect(option);
                    }}
                    aria-pressed={active}
                    aria-disabled={disabled}
                    title={
                      !disabled
                        ? getOptionDisplayLabel(option)
                        : purchaseState.message || purchaseState.label
                    }
                  >
                    <span>{getOptionDisplayLabel(option)}</span>
                    <small>{purchaseState.label}</small>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {showQty ? (
          <section className="purchase-box__section purchase-box__section--qty">
            <div className="purchase-box__section-head">
              <h2>Quantity</h2>
              <strong>{safeQty}</strong>
            </div>

            {safeMaxQty > 1 ? (
              <p className="purchase-box__micro-copy">Maximum {safeMaxQty}</p>
            ) : null}

            <div className="purchase-box__quantity-control">
              <button
                type="button"
                className="purchase-box__quantity-button"
                disabled={!canDecreaseQty}
                onClick={() => handleQtyChange(safeQty - 1)}
                aria-label="Decrease quantity"
              >
                <Minus size={16} />
              </button>

              <span className="purchase-box__quantity-value" aria-live="polite">
                {safeQty}
              </span>

              <button
                type="button"
                className="purchase-box__quantity-button"
                disabled={!canIncreaseQty}
                onClick={() => handleQtyChange(safeQty + 1)}
                aria-label="Increase quantity"
              >
                <Plus size={16} />
              </button>
            </div>
          </section>
        ) : null}

        {resolveError ? (
          <p className="purchase-box__error">{resolveError}</p>
        ) : null}

        {basketFeedback.message ? (
          <div
            className={clsx(
              "purchase-box__feedback",
              basketFeedback.tone === "success" && "is-success",
              basketFeedback.tone === "danger" && "is-danger",
              basketFeedback.tone === "info" && "is-info",
            )}
            aria-live="polite"
          >
            <div>
              <strong>
                {basketFeedback.tone === "success"
                  ? "Basket updated"
                  : basketFeedback.tone === "info"
                    ? "Basket"
                    : "Basket issue"}
              </strong>
              <span>{basketFeedback.message}</span>
            </div>

            {basketFeedback.tone === "success" ||
            basketFeedback.tone === "info" ? (
              <Link to={CART_URL} className="purchase-box__feedback-link">
                View Basket
              </Link>
            ) : null}
          </div>
        ) : null}

        {madeToMeasureFeedback ? (
          <div className="purchase-box__feedback is-info" aria-live="polite">
            <div>
              <strong>Made to measure</strong>
              <span>{madeToMeasureFeedback}</span>
            </div>
          </div>
        ) : null}

        <div
          className={clsx(
            "purchase-box__actions",
            !showSaveButton && "purchase-box__actions--single",
          )}
        >
          {showSaveButton ? (
            <button
              type="button"
              className={clsx("purchase-box__save", { "is-saved": isSaved })}
              aria-label={isSaved ? "Saved product" : "Save product"}
              aria-pressed={isSaved}
              disabled={saveDisabled}
              onClick={onSaveProduct}
            >
              <Heart size={18} fill={isSaved ? "currentColor" : "none"} />
              <span>{isSaved ? "Saved" : "Save for later"}</span>
            </button>
          ) : null}

          {showMadeToMeasureAction ? (
            <button
              type="button"
              className="purchase-box__add"
              disabled={!madeToMeasureCanSelect || madeToMeasureLoading}
              onClick={handleSelectForMadeToMeasure}
            >
              {madeToMeasureLoading
                ? "Saving..."
                : madeToMeasureStepLabel || "Choose for Made To Measure"}
            </button>
          ) : shouldShowBasketAction ? (
            <Link
              to={CART_URL}
              className="purchase-box__add purchase-box__add--link"
            >
              {addButtonLabel}
            </Link>
          ) : (
            <button
              type="button"
              className="purchase-box__add"
              disabled={!canPurchase}
              onClick={handleAddToBasket}
            >
              {addButtonLabel}
            </button>
          )}
        </div>

        <div className="purchase-box__support-note">
          <ShoppingBag size={17} aria-hidden="true" />
          <p>
            {showMadeToMeasureAction
              ? "Made to measure selections are saved to your request draft. Final quote is confirmed by our team."
              : "General UK delivery and returns policy applies. Final delivery options are shown at checkout."}
          </p>
        </div>

        <div className="purchase-box__details">
          <AccordionRow title="Description" defaultOpen>
            {introDescription ? (
              <p>{introDescription}</p>
            ) : (
              <p>
                A carefully selected piece. More product information will be
                added soon.
              </p>
            )}
          </AccordionRow>

          {productInformationRows.length ? (
            <AccordionRow title="Product Information">
              <dl className="purchase-box__spec-list">
                {productInformationRows.map(([label, value], index) => (
                  <div key={`${label}-${value}-${index}`}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </AccordionRow>
          ) : null}

          {productCode ? (
            <p className="purchase-box__product-code">
              <strong>Product Code:</strong> {productCode}
            </p>
          ) : null}
        </div>
      </aside>

      <div className="purchase-box__mobile-bar">
        <div className="purchase-box__mobile-meta">
          <strong>{formatMoney(selectedPrice)}</strong>
          <span>{selectedSummary || statusMeta.label}</span>
        </div>

        {showMadeToMeasureAction ? (
          <button
            type="button"
            className="purchase-box__mobile-add"
            disabled={!madeToMeasureCanSelect || madeToMeasureLoading}
            onClick={handleSelectForMadeToMeasure}
          >
            <ShoppingBag size={16} />
            {madeToMeasureLoading
              ? "Saving..."
              : madeToMeasureStepLabel || "Choose for MTM"}
          </button>
        ) : shouldShowBasketAction ? (
          <Link
            to={CART_URL}
            className="purchase-box__mobile-add purchase-box__mobile-add--link"
          >
            <ShoppingBag size={16} />
            {addButtonLabel}
          </Link>
        ) : (
          <button
            type="button"
            className="purchase-box__mobile-add"
            disabled={!canPurchase}
            onClick={handleAddToBasket}
          >
            <ShoppingBag size={16} />
            {addButtonLabel}
          </button>
        )}
      </div>
    </>
  );
}
